import { FC, useEffect, useRef, useState } from "react";
import { ArrowLeft, Book, Search, X } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ScrollArea } from "@capacit-com/lara-law-shared-ui";
import useLawDictionaryStore from "@/stores/law-dictionary-control.store";
import { cn } from "@/lib/utils";
import { LawTerm } from "@/models/law-term/LawTerm";
import useLawTermToggleStore from "@/stores/lawtermtoggle.store";
import { useLawTermsContext } from "@/providers/law-terms/LawTermsContext";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

type LawDictionaryModalProps = {
  disabled: boolean;
};

const LawDictionaryModal: FC<LawDictionaryModalProps> = ({ disabled }) => {
  const {
    isDialogOpen,
    searchTerm,
    selectedLetter,
    selectedTerm,
    setIsDialogOpen,
    setSelectedLetter,
    setSearchTerm,
    setSelectedTerm,
  } = useLawDictionaryStore();
  const { allLawTerms } = useLawTermsContext();

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ".split("");

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [lawTerms, setLawTerms] = useState<LawTerm[]>([]);
  const [termHistory, setTermHistory] = useState<
    { lawTermHistory: LawTerm[]; searchTermHistory: string[] }[]
  >([]);
  const [isFollowingReference, setIsFollowingReference] = useState(false);
  const { toggleLawTermShow, setToggleLawTermShow } = useLawTermToggleStore();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const selectedButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current!.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [lawTerms, selectedLetter]);

  useEffect(() => {
    if (
      isDialogOpen &&
      selectedTerm &&
      selectedLetter.toLowerCase() !== "alle"
    ) {
      const firstLetter = selectedTerm.term[0].toUpperCase();
      setSelectedLetter(firstLetter);
      setLawTerms(
        allLawTerms
          ?.filter((term) => term.term.startsWith(firstLetter))
          .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
      );
      setTimeout(() => {
        selectedButtonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, selectedTerm, allLawTerms]);

  useEffect(() => {
    if (isFollowingReference) {
      setTimeout(() => {
        selectedButtonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
      setIsFollowingReference(false);
    }
  }, [isFollowingReference]);

  useEffect(() => {
    setLawTerms(
      allLawTerms?.sort((a, b) => a.term.localeCompare(b.term, "da")) || []
    );
  }, [allLawTerms]);

  // Update the term click handler in definitionHandler
  const handleTermReferenceClick = (referencedTerm: string) => {
    const termToSelect = allLawTerms?.find((term) =>
      term.term.includes(referencedTerm)
    );
    if (termToSelect) {
      setIsFollowingReference(true);
      if (selectedTerm) {
        setTermHistory((prev) => [
          ...prev,
          { lawTermHistory: [selectedTerm], searchTermHistory: [searchTerm] },
        ]);
      }
      setSelectedTerm(termToSelect);
      setSearchTerm("");

      if (selectedLetter !== "alle") {
        const firstLetter = termToSelect.term[0].toUpperCase();
        if (firstLetter !== selectedLetter) {
          setSelectedLetter(firstLetter);
        }
        setLawTerms(
          allLawTerms
            ?.filter((term) => term.term.startsWith(firstLetter))
            .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
        );
      } else {
        setLawTerms(
          allLawTerms?.sort((a, b) => a.term.localeCompare(b.term, "da")) || []
        );
      }
    }
  };

  const handleBackButtonClick = () => {
    setSelectedTerm(termHistory[termHistory.length - 1].lawTermHistory[0]);
    setSearchTerm(termHistory[termHistory.length - 1].searchTermHistory[0]);
    setTermHistory((prev) => prev.slice(0, -1));
    if (selectedLetter !== "alle") {
      setSelectedLetter(
        termHistory[
          termHistory.length - 1
        ].lawTermHistory[0].term[0].toUpperCase()
      );

      setLawTerms(
        allLawTerms
          ?.filter(
            (term) =>
              term.term
                .toLowerCase()
                .includes(
                  termHistory[
                    termHistory.length - 1
                  ].searchTermHistory[0].toLowerCase()
                ) &&
              term.term.startsWith(
                termHistory[
                  termHistory.length - 1
                ].lawTermHistory[0].term[0].toUpperCase()
              )
          )
          .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
      );
    } else {
      setLawTerms(
        allLawTerms
          ?.filter((term) =>
            term.term
              .toLowerCase()
              .includes(
                termHistory[
                  termHistory.length - 1
                ].searchTermHistory[0].toLowerCase()
              )
          )
          .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
      );
    }

    setIsFollowingReference(true);
  };

  const definitionHandler = (definition: string) => {
    if (!definition.toLowerCase().match(/\(?se {/i)) return <>{definition}</>;

    const parts = definition.split(/(\(?se {)/i);
    const beforeRef = parts[0];
    const afterRef = parts[2].split("}");
    const referencedTerm = afterRef[0];
    const remainingText = afterRef[1];

    return (
      <>
        {beforeRef}
        {parts[1].replace("{", "'")}
        <button
          onClick={() => handleTermReferenceClick(referencedTerm)}
          className="text-primary hover:underline font-medium"
        >
          {referencedTerm}
        </button>
        {"}".replace("}", "'")}
        {remainingText}
      </>
    );
  };

  const handleSearchLawTerms = (searchedWord: string) => {
    setSearchTerm(searchedWord);
    if (searchedWord.length > 0) {
      setLawTerms(
        allLawTerms
          ?.filter((item) =>
            item.term.toLowerCase().includes(searchedWord.toLowerCase())
          )
          .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
      );
    } else {
      setLawTerms(allLawTerms || []);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setLawTerms(allLawTerms || []);
  };

  const handleLetterChange = (letter: string) => {
    setSelectedLetter(letter);
    setTermHistory([]);
    setLawTerms(
      allLawTerms
        ?.filter(
          (term) =>
            term.term.toLowerCase().includes(searchTerm.toLowerCase()) &&
            term.term.startsWith(letter)
        )
        .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
    );
  };

  const handleAllSelected = () => {
    setSelectedLetter("alle");
    setTermHistory([]);
    setLawTerms(
      allLawTerms
        ?.filter((term) =>
          term.term.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.term.localeCompare(b.term, "da")) || []
    );
  };

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(open) => {
        if (!disabled) {
          setIsDialogOpen(open);
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger
          asChild
          onPointerEnter={disabled ? undefined : () => setTooltipVisible(true)}
          onPointerLeave={disabled ? undefined : () => setTooltipVisible(false)}
        >
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="px-2.5 py-5"
              disabled={disabled}
            >
              <Book
                style={{ width: "20px", height: "20px" }}
                className={cn(
                  isDialogOpen && "text-primary",
                  "prompt-library-icon"
                )}
              />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        {tooltipVisible && (
          <TooltipContent>
            <p>Juridisk ordbog</p>
          </TooltipContent>
        )}
      </Tooltip>

      <DialogContent size="xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Juridisk ordbog</DialogTitle>
          </div>
          <DialogDescription>
            <span className="flex items-center justify-between">
              Søg efter juridiske termer og definitioner
              <span className="flex items-center space-x-2">
                <span className="text-sm text-foreground">
                  Vis juridiske termer i chatten
                </span>
                <Checkbox
                  checked={toggleLawTermShow}
                  onCheckedChange={() =>
                    setToggleLawTermShow(!toggleLawTermShow)
                  }
                />
              </span>
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-2 relative">
            <Search className="size-4 text-muted-foreground" />
            <Input
              role="searchbox"
              value={searchTerm}
              onChange={(e) => handleSearchLawTerms(e.target.value)}
              placeholder="Søg..."
              className="flex-1 mr-0"
            />
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 p-0 size-6 flex items-center justify-center",
                searchTerm
                  ? "opacity-100 transition-opacity duration-200"
                  : "opacity-0 transition-none pointer-events-none"
              )}
              onClick={handleClearSearch}
            >
              <X className="size-4" />
            </Button>
          </div>

          <div className="flex gap-1 flex-wrap pb-2">
            <Button
              key={"alle"}
              variant={selectedLetter === "alle" ? "default" : "outline"}
              className="size-10"
              onClick={handleAllSelected}
            >
              Alle
            </Button>
            {alphabet.map((letter) => (
              <Button
                key={letter}
                variant={selectedLetter === letter ? "default" : "outline"}
                className="size-10"
                onClick={() => handleLetterChange(letter)}
              >
                {letter}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ScrollArea
              ref={scrollAreaRef}
              className="h-[300px] border rounded-md p-2 w-full"
            >
              <style>
                {" "}
                {`[data-radix-scroll-area-viewport] div { display:block !important; }`}{" "}
              </style>

              {lawTerms.length === 0 && (
                <p className="text-muted-foreground pl-4 pt-2 text-md">
                  Ingen resultater
                </p>
              )}
              {lawTerms
                .filter((item) =>
                  selectedLetter === "alle"
                    ? true
                    : item.term.startsWith(selectedLetter)
                )
                .sort((a, b) => a.term.localeCompare(b.term, "da"))
                .map((item) => (
                  <div
                    key={item.term}
                    className="flex justify-center items-center h-full mr-2 max-w-full"
                  >
                    <Button
                      key={item.term}
                      ref={
                        selectedTerm?.term === item.term
                          ? selectedButtonRef
                          : null
                      }
                      variant="ghost"
                      className={cn(
                        "w-full justify-start text-ellipsis overflow-hidden block! text-left",
                        selectedTerm?.term === item.term &&
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      )}
                      onClick={() => {
                        setSelectedTerm(item);
                        setTermHistory([]);
                      }}
                    >
                      {item.term}
                    </Button>
                  </div>
                ))}
            </ScrollArea>
            <ScrollArea className="h-[300px] border rounded-md p-4">
              {selectedTerm ? (
                <>
                  <div className="flex gap-2 mb-2 justify-between">
                    <h3 className="text-lg font-semibold flex items-center">
                      {selectedTerm.term}
                    </h3>

                    {termHistory.length > 0 && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleBackButtonClick}
                        >
                          <ArrowLeft />
                          Tilbage
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {definitionHandler(selectedTerm.definition)}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Vælg et term for at se definitionen
                </p>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LawDictionaryModal;
